// --- Dependencies -------------------------------------------------------------------------------
import { useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyConfirmDetails = {
    title: string;
    message: string;
    confirmLabel?: string;
    danger?: boolean;
    /** When set, the button stays dead until this exact word is typed. For the irreversible ones. */
    confirmWord?: string;
    onConfirm: () => void;
};

type Props = { onClose: () => void; details?: unknown };

/** One confirmation for the whole app. Destructive by default, because that is what gets confirmed. */
export default function ConfirmPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const confirm = details as TyConfirmDetails;
    const [typed, setTyped] = useState("");

    const isBlocked = Boolean(confirm.confirmWord) && typed.trim() !== confirm.confirmWord;

    const handleConfirm = () => {
        if (isBlocked) return;
        onClose();
        confirm.onConfirm();
    };

    return (
        <section className={`popup-card confirm-popup${confirm.danger === false ? "" : " confirm-popup--danger"}`}>
            <h2 className="confirm-popup__title">{confirm.title}</h2>
            <p className="confirm-popup__message">{confirm.message}</p>

            {confirm.confirmWord && (
                <label className="confirm-popup__typed">
                    <span>{t.common.typeToConfirm.replace("{word}", confirm.confirmWord)}</span>
                    <input className="confirm-popup__input" value={typed} onChange={(event) => setTyped(event.target.value)} />
                </label>
            )}

            <div className="confirm-popup__actions">
                <button type="button" className="confirm-popup__cancel" onClick={onClose}>
                    {t.common.cancel}
                </button>
                <button type="button" className="confirm-popup__confirm" onClick={handleConfirm} disabled={isBlocked}>
                    {confirm.confirmLabel ?? t.common.confirm}
                </button>
            </div>
        </section>
    );
}
